import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';

/**
 * Suchbares Auswahlfeld (Combobox) — erfüllt die Repo-UI-Regel: jedes
 * Dropdown mit mehr als 4 Text-/Namens-Optionen MUSS tippbar filterbar sein.
 *
 * API bewusst kompatibel zu einem schlanken `<select value onChange>`:
 *   - `value` / `onChange(value)` arbeiten auf dem Options-Wert (string).
 *   - `options` liefert Wert + Label (+ optionalen Sublabel-Text).
 *
 * Tailwind, ohne Fremd-Lib, kalkül-Brand (Pill-Trigger, Cyan-Fokus).
 */
export interface SearchableOption {
  value: string;
  label: string;
  sublabel?: string;
}

interface Props {
  options: SearchableOption[];
  value: string | null;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  /** Optionaler Reset auf „nichts gewählt". Wird nur angezeigt, wenn gesetzt. */
  onClear?: () => void;
  className?: string;
  /** Menü nach oben öffnen (z.B. in einer Compose-Leiste am unteren Rand). */
  openUp?: boolean;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Auswählen…',
  searchPlaceholder = 'Suchen…',
  emptyText = 'Keine Treffer.',
  disabled = false,
  onClear,
  className = '',
  openUp = false,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const selected = options.find((o) => o.value === value) ?? null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) || (o.sublabel?.toLowerCase().includes(q) ?? false),
    );
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      // Fokus erst nach dem Öffnen setzen.
      const id = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(id);
    }
  }, [open]);

  useEffect(() => setActiveIndex(0), [query]);

  // activeIndex gegen die aktuelle Trefferliste klemmen (0..len-1, bei leerer Liste 0) —
  // schützt Tastatur-Enter und das Highlight, falls der Filter Treffer wegfallen lässt.
  const safeActiveIndex =
    filtered.length === 0 ? 0 : Math.min(Math.max(activeIndex, 0), filtered.length - 1);

  function pick(opt: SearchableOption) {
    onChange(opt.value);
    setOpen(false);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(() => Math.min(safeActiveIndex + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(() => Math.max(safeActiveIndex - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = filtered[safeActiveIndex];
      if (opt) pick(opt);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div ref={rootRef} className={['relative', className].join(' ')}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={[
          'flex h-9 w-full items-center justify-between gap-2 rounded-field border px-3 text-left text-sm transition-all',
          'border-kalkuel-border bg-white text-kalkuel-primary hover:border-kalkuel-accent-strong',
          'focus:border-kalkuel-accent focus:outline-none focus:shadow-focus-accent',
          'disabled:cursor-not-allowed disabled:opacity-50',
        ].join(' ')}
      >
        <span className={['truncate', selected ? '' : 'text-kalkuel-muted'].join(' ')}>
          {selected ? selected.label : placeholder}
        </span>
        <span className="flex shrink-0 items-center gap-1">
          {onClear && selected && (
            <X
              size={13}
              className="text-kalkuel-muted hover:text-kalkuel-primary"
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
            />
          )}
          <ChevronDown size={14} className="text-kalkuel-muted" />
        </span>
      </button>

      {open && (
        <div
          className={[
            'absolute z-30 w-full rounded-card border border-kalkuel-border bg-white shadow-pop',
            openUp ? 'bottom-full mb-1 animate-slide-up' : 'top-full mt-1 animate-slide-down',
          ].join(' ')}
        >
          <div className="relative border-b border-kalkuel-border p-2">
            <Search
              size={13}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-kalkuel-muted"
            />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={searchPlaceholder}
              className="h-8 w-full rounded-field border border-kalkuel-border bg-kalkuel-surface pl-8 pr-3 text-sm placeholder:text-kalkuel-muted focus:border-kalkuel-accent focus:outline-none focus:shadow-focus-accent"
            />
          </div>
          <ul className="max-h-60 overflow-y-auto py-1" role="listbox">
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-xs text-kalkuel-muted">{emptyText}</li>
            )}
            {filtered.map((opt, idx) => {
              const isSelected = opt.value === value;
              const isActive = idx === safeActiveIndex;
              return (
                <li key={opt.value} role="option" aria-selected={isSelected}>
                  <button
                    type="button"
                    onMouseEnter={() => setActiveIndex(idx)}
                    onClick={() => pick(opt)}
                    className={[
                      'flex w-full items-center justify-between gap-2 px-3 py-2 text-left',
                      isActive ? 'bg-kalkuel-accent-soft' : 'hover:bg-kalkuel-surface',
                    ].join(' ')}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-kalkuel-primary">
                        {opt.label}
                      </span>
                      {opt.sublabel && (
                        <span className="block truncate text-[11px] text-kalkuel-muted">
                          {opt.sublabel}
                        </span>
                      )}
                    </span>
                    {isSelected && (
                      <Check size={14} className="shrink-0 text-kalkuel-accent-strong" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
